import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";

/**
 * DoorbellPopup — silently polls /jarvis/ring/unseen every 15s. When a
 * NEW Ring event (ding/motion) fires, slides in a cinematic snapshot card
 * from the top-right of the screen with an audio ping. Click ✕ to dismiss
 * or auto-dismisses after 90s.
 *
 * No keys configured → polls become 200/{configured:false} → component
 * silently sleeps. Once RING_REFRESH_TOKEN is added in Settings → API
 * Keys, the popup activates automatically with no code change.
 */
export default function DoorbellPopup() {
  const [event, setEvent] = useState(null);
  const lastSeenRef = useRef(localStorage.getItem("rc_ring_last_seen") || "");
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get("/jarvis/ring/unseen", { params: { since: lastSeenRef.current } });
        if (!alive) return;
        const events = r.data?.events || [];
        if (events.length === 0) return;
        const fresh = events[0]; // newest first
        if (fresh.id && fresh.id !== lastSeenRef.current) {
          lastSeenRef.current = fresh.id;
          localStorage.setItem("rc_ring_last_seen", fresh.id);
          setEvent(fresh);
          // Audio chime — descending ding-dong
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              [{ f: 1320, t: 0 }, { f: 880, t: 0.28 }].forEach(({ f, t }) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = "sine"; o.frequency.value = f;
                g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
                g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.03);
                g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5);
                o.connect(g); g.connect(ctx.destination);
                o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.6);
              });
              setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
            }
          } catch {}
          clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = setTimeout(() => setEvent(null), 90000);
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(id); clearTimeout(dismissTimerRef.current); };
  }, []);

  if (!event) return null;
  const kind = (event.kind || "ding").toUpperCase();
  const t = event.created_at ? new Date(event.created_at).toLocaleTimeString() : "";
  return (
    <div data-testid="doorbell-popup" style={{
      position: "fixed", top: 80, right: 24, width: 320, zIndex: 100,
      background: "rgba(0, 4, 12, 0.94)",
      border: "1px solid var(--rc-gold)",
      borderRadius: 12,
      boxShadow: "0 0 32px rgba(212, 175, 55, 0.4), 0 8px 24px rgba(0,0,0,0.6)",
      backdropFilter: "blur(12px)",
      animation: "doorbell-slide-in 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
      color: "var(--rc-text)", fontFamily: "var(--rc-font-display)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid var(--rc-gold-dim)" }}>
        <span style={{ fontSize: 11, color: "var(--rc-gold)", letterSpacing: 2 }}>🔔 RING · {kind}</span>
        <button data-testid="doorbell-dismiss" onClick={() => setEvent(null)} style={{ background: "transparent", border: "none", color: "var(--rc-text-dim)", fontSize: 18, cursor: "pointer" }}>✕</button>
      </div>
      {event.snapshot_url ? (
        <img src={event.snapshot_url} alt="doorbell snapshot" style={{ width: "100%", display: "block" }} />
      ) : (
        <div style={{ padding: 32, textAlign: "center", fontSize: 40 }}>🚪</div>
      )}
      <div style={{ padding: "10px 14px", fontSize: 11, color: "var(--rc-text-dim)" }}>
        {event.device || "Doorbell"} · {t}
      </div>
      <style>{`
        @keyframes doorbell-slide-in {
          from { opacity: 0; transform: translateX(60px) scale(0.96); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
